import { useEffect, useState, VFC } from "react";
import GenericTemplate from "@template/index";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
  Typography,
  MenuItem,
} from "@mui/material";
import {
  DraggableProvided,
  DroppableProvided,
  DropResult,
  Draggable,
  Droppable,
  DragDropContext,
} from "react-beautiful-dnd";
import { AddCircle, Delete, Edit } from "@mui/icons-material";
import messages from "config/messages";
import { sort, getUserInfo } from "@utils/index";
import HolidayEditDialog, { IHolidayEditData } from "./HolidayEditDialog";
import { getListActivityBase } from "@api/groupMaster";
import {
  deleteHoliday,
  getListHoliday,
  updateHolidaySort,
} from "@api/itemReport";
import { insertMaster, updateMaster } from "@api/master";
import LoadingOverlayController from "@shared-components/loading/LoadingOverlayController";
import ModalController from "@shared-components/modal/ModalController";
import { USER_ROLES } from "@shared-constants";
import DatePickerCustom from "components/atoms/DatePickerCustom";
import SelectLabel from "components/atoms/SelectLabel";
import formatDateToString from "@utils/DateFormat";
import MasterExportButton from "components/organisms/MasterExportButton";
import { ID_HOLIDAY } from "shared/constants/MasterInfo";

const initial_data: IHolidayEditData = {
  year: "",
  holiday: "",
};

const HolidayEditScreen: VFC = () => {
  // ------------------------------------------------------------------
  // 初期化
  // ------------------------------------------------------------------
  const [data, setData] = useState<any[]>([]);
  const [originData, setOriginData] = useState<any[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<number>(-1);
  const [editData, setEditData] = useState<IHolidayEditData>(initial_data);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectDisabled, setSelectDisabled] = useState<boolean>(true);
  const [listGroup, setListGroup] = useState([]);
  const [activityBaseId, setActivityBaseId] = useState<string>("");
  const [userRole, setUserRole] = useState<string>("");

  const [year, setYear] = useState<string>(new Date().getFullYear().toString());

  const user_info = getUserInfo();

  useEffect(() => {
    getListGroup();
  }, []);

  const getListGroup = async (keyword?: string) => {
    setUserRole(user_info.user_role);
    if (user_info.user_role == USER_ROLES.OWNER.value) {
      await getListActivityBase(keyword)
        .then((res) => {
          if (res) {
            setListGroup(res);
          }
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      setActivityBaseId(user_info.location_id);
    }
  };

  const handleDnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    const sorted = sort(data, result.source.index, result.destination.index);
    setData(sorted);
  };

  const handleAdd = () => {
    setEditData({ ...initial_data, year });
    setOpen(true);
  };

  const handleDelete = (SK: string) => {
    if (!ModalController.isShowing()) {
      const confirmMessage = disabled
        ? messages.COMMON.MSG_COMMON_DELETE_CONFIRM_001
        : messages.COMMON.MSG_COMMON_DELETE_CONFIRM_SORT_001;
      ModalController.show({
        message: confirmMessage,
        visibleButton1: true,
        visibleButton2: true,
        handlePressButton2: () => handleDeleteSettings(SK),
      });
    }
    return;
  };

  const handleDeleteSettings = async (SK: string) => {
    try {
      LoadingOverlayController.show();
      const res = await deleteHoliday(SK);
      if (res) {
        ModalController.show({
          message: messages.COMMON.MSG_COMMON_DELETE_SUCCESS_001("祝日マスタ"),
          visibleButton2: true,
          handlePressButton2: () => {
            fetchData();
          },
        });
      }
    } catch (error: any) {
      if (!ModalController.isShowing())
        ModalController.show({
          message: error?.detail,
          visibleButton2: true,
        });
      console.log("error handleDeleteSettings", error);
    } finally {
      LoadingOverlayController.hide();
    }
  };

  const handleEdit = (d: any, i: number) => {
    setEditData({
      year: year,
      holiday: d.holiday,
    });
    setEditIndex(i);
    showConfirm(setOpen);
  };

  const showConfirm = (f: (flg: boolean) => void) => {
    if (!ModalController.isShowing() && !disabled) {
      ModalController.show({
        message: messages.COMMON.MSG_COMMON_UPDATE_CONFIRM_SORT_001,
        visibleButton1: true,
        visibleButton2: true,
        handlePressButton2: () => {
          f(true);
        },
      });
    } else {
      f(true);
    }
  };

  const handleChange = async (value: IHolidayEditData) => {
    let newData = JSON.parse(JSON.stringify(data));
    let message = "";
    let res = null;
    const formData = {
      type: "HOLIDAY",
      name: value.holiday,
      color: "",
      color_id: "",
      color_name: "",
      index: "",
      parent: "",
      activity_base_id: activityBaseId,
      is_manual_input: false,
      is_default: false,
    };
    try {
      LoadingOverlayController.show();
      if (editIndex >= 0) {
        res = await updateMaster(formData, newData[editIndex].SK);
        if (res) {
          message = "祝日マスタの更新";
        }
      } else {
        res = await insertMaster(formData);
        if (res) {
          message = "祝日マスタの登録";
        }
      }
      setEditIndex(-1);
      if (res) {
        setData(newData);
        ModalController.show({
          message: messages.COMMON.MSG_COMMON_SUCCESS_001(message),
          visibleButton2: true,
          handlePressButton2: () => {
            fetchData();
          },
        });
      }
    } catch (error: any) {
      if (!ModalController.isShowing())
        ModalController.show({
          message: error?.detail,
          visibleButton2: true,
        });
      console.log("error handleChange", error);
    } finally {
      LoadingOverlayController.hide();
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setEditIndex(-1);
  };

  const handleSave = async () => {
    try {
      LoadingOverlayController.show();
      let formData: any[] = [];
      data.map((item) => {
        const datilData = {
          PK: item.PK,
          SK: item.SK,
        };
        formData.push(datilData);
      });
      const res = await updateHolidaySort(formData);
      if (res) {
        ModalController.show({
          message:
            messages.COMMON.MSG_COMMON_SUCCESS_001("祝日マスタの並び順の更新"),
          visibleButton2: true,
          handlePressButton2: () => {
            fetchData();
          },
        });
      }
    } catch (error: any) {
      if (!ModalController.isShowing())
        ModalController.show({
          message: error?.detail,
          visibleButton2: true,
        });
      console.log("error handleSave", error);
    } finally {
      LoadingOverlayController.hide();
    }
  };

  const fetchData = async () => {
    LoadingOverlayController.show();
    setLoading(true);

    await getListHoliday(year.padStart(4, "0"), activityBaseId)
      .then((res) => {
        if (res?.data) {
          setData(res.data);
          setOriginData(res.data);
        }
      })
      .finally(() => {
        LoadingOverlayController.hide();
        setLoading(false);
      });
  };

  const fetchDataConfirm = async () => {
    if (!ModalController.isShowing() && !year) {
      ModalController.show({
        message: messages.COMMON.ERROR.MSG_RQUIRED_SELECT("対象年"),
        visibleButton2: true,
      });
    }
    if (!ModalController.isShowing() && !activityBaseId) {
      ModalController.show({
        message: messages.COMMON.ERROR.MSG_RQUIRED_SELECT("拠点"),
        visibleButton2: true,
      });
    }
    if (!ModalController.isShowing() && !disabled) {
      ModalController.show({
        message: messages.COMMON.MSG_COMMON_DISPLAY_CONFIRM_SORT_001,
        visibleButton1: true,
        visibleButton2: true,
        handlePressButton2: () => {
          fetchData();
          setData([]);
          setOriginData([]);
          setSelectDisabled(true);
        },
      });
    }
    if (disabled) {
      if (selectDisabled) {
        if (year && activityBaseId) {
          setSelectDisabled(false);
          fetchData();
        }
      } else {
        setData([]);
        setOriginData([]);
        setSelectDisabled(true);
      }
    }
  };

  useEffect(() => {
    setDisabled(JSON.stringify(data) === JSON.stringify(originData));
  }, [data, originData]);

  return (
    <GenericTemplate title="祝日マスタ管理">
      {/* 編集ダイアログ */}
      <HolidayEditDialog
        data={editData}
        open={open}
        onChange={handleChange}
        onClose={handleCancel}
      />

      {userRole == USER_ROLES.OWNER.value && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            mt: 3,
            mb: 3,
          }}
        >
          <SelectLabel
            label="拠点"
            value={activityBaseId}
            isIgnoreExtractRuleLabel={false}
            isIgnoreExtractRuleSelect={true}
            onChange={(e) => setActivityBaseId(e.target.value)}
            sx={{ width: 500 }}
            disabled={!selectDisabled}
          >
            {listGroup.map((item: any) => (
              <MenuItem value={item.SK} key={item.SK}>
                <span className="IgnoreExtractRuleTarget">
                  {item.activity_base_name}
                </span>
              </MenuItem>
            ))}
          </SelectLabel>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          mt: 3,
          mb: 3,
        }}
      >
        <DatePickerCustom
          label={"対象年"}
          value={year}
          onChange={(value) => {
            // 入力値を年(yyyy)に成型
            let new_value = "";
            if (value) {
              const date = new Date(value || "");
              if (!Number.isNaN(date.getTime())) {
                new_value = date.getFullYear().toString();
              }
            }
            setYear(new_value);
          }}
          openTo="year"
          views={["year"]}
          inputFormat="yyyy"
          mask="____"
          disabled={!selectDisabled}
        />
        <Button
          onClick={fetchDataConfirm}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            mt: 3,
            ml: 2,
          }}
        >
          {selectDisabled ? "選択" : "解除"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="body2">ドラッグ＆ドロップで並べ替え</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <MasterExportButton
          masterType={ID_HOLIDAY}
          activityBaseId={activityBaseId}
          userInfo={user_info}
          isDisabled={false}
        />
        <Button
          onClick={handleAdd}
          disabled={selectDisabled}
          endIcon={<AddCircle />}
        >
          追加
        </Button>
      </Box>
      <Box>
        <DragDropContext onDragEnd={handleDnd}>
          <Droppable droppableId="droppableId-1">
            {(provided: DroppableProvided) => (
              <List ref={provided.innerRef} {...provided.droppableProps}>
                {data.map((item, i) => (
                  <Draggable key={item.SK} draggableId={item.SK} index={i}>
                    {(provided: DraggableProvided) => (
                      <ListItem
                        sx={{ bgcolor: "white" }}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        secondaryAction={
                          <>
                            <Tooltip title="編集">
                              <IconButton
                                edge="end"
                                sx={{ mx: 1 }}
                                onClick={() => {
                                  handleEdit(item, i);
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="削除">
                              <IconButton
                                edge="end"
                                onClick={() => handleDelete(item.SK)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        }
                        divider
                      >
                        <span className="IgnoreExtractRuleTarget">
                          <ListItemText
                            primary={formatDateToString(item.holiday, "YMD_sl")}
                            primaryTypographyProps={{
                              style: {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                marginRight: "40px",
                              },
                            }}
                          />
                        </span>
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
        {data?.length == 0 && !loading && !selectDisabled && (
          <Typography>{messages.COMMON.MSG_NOT_EXIST("検索結果")}</Typography>
        )}
      </Box>
      <Box sx={{ height: 30 }} />
      <Box
        sx={{
          position: "fixed",
          margin: 0,
          right: 0,
          bottom: 20,
          textAlign: "center",
          width: "100%",
        }}
      >
        <Button onClick={handleSave} color="secondary" disabled={disabled}>
          保存
        </Button>
      </Box>
    </GenericTemplate>
  );
};

export default HolidayEditScreen;
